import { useMemo } from "react";
import { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";
import {
    ScheduleApplicationStatus,
    ScheduleLifecycleVerb,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { getAvailableScheduleLifecycleVerbs } from "./applications/scheduleApplicationStatusDisplay";

export interface ScheduleLifecycleRequests {
    onChangeSchedule?: () => void;
    onResumeSchedule?: () => void;
}

interface ScheduleLifecycleRequestsOptions {
    applicationId: string | null;
    status: ScheduleApplicationStatus | null;
    /** Defaults to false until the applied summaries carry the flag. */
    isMilestone?: boolean;
    entityId: number | null | undefined;
    sourceType: ScheduleSourceType;
    requestLifecycleAction:
        | ((request: ScheduleLifecycleRequest) => void)
        | undefined;
}

/**
 * The lifecycle actions a Schedule cell offers on its applied schedule:
 * Change while it is recognizing or once it has completed, Resume while it
 * is paused. Each is absent
 * when the schedule cannot take it or the page cannot raise it.
 */
export function buildScheduleLifecycleRequests(
    options: ScheduleLifecycleRequestsOptions,
): ScheduleLifecycleRequests {
    const {
        applicationId,
        status,
        isMilestone = false,
        entityId,
        sourceType,
        requestLifecycleAction,
    } = options;
    if (applicationId == null || entityId == null || !requestLifecycleAction) {
        return {};
    }
    const request = (verb: ScheduleLifecycleVerb) => () =>
        requestLifecycleAction({
            applicationId,
            entityId,
            sourceType,
            verb,
        });
    return {
        onChangeSchedule:
            status !== null &&
            getAvailableScheduleLifecycleVerbs({
                status,
                isMilestone,
            }).includes(ScheduleLifecycleVerb.change)
                ? request(ScheduleLifecycleVerb.change)
                : undefined,
        onResumeSchedule:
            status === ScheduleApplicationStatus.paused
                ? request(ScheduleLifecycleVerb.resume)
                : undefined,
    };
}

export function useScheduleLifecycleRequests(
    options: ScheduleLifecycleRequestsOptions,
): ScheduleLifecycleRequests {
    const {
        applicationId,
        status,
        isMilestone = false,
        entityId,
        sourceType,
        requestLifecycleAction,
    } = options;
    return useMemo(
        () =>
            buildScheduleLifecycleRequests({
                applicationId,
                status,
                isMilestone,
                entityId,
                sourceType,
                requestLifecycleAction,
            }),
        [
            applicationId,
            status,
            isMilestone,
            entityId,
            sourceType,
            requestLifecycleAction,
        ],
    );
}
